import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Brain, TrendingUp, TrendingDown, Minus, Sparkles, RefreshCw } from 'lucide-react';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MarketAnalysis = () => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysisType, setAnalysisType] = useState(null); // 'advanced' or 'beginner'

  const generateAnalysis = async (type = 'advanced') => {
    try {
      setLoading(true);
      const endpoint = type === 'beginner' ? `${API}/analysis/beginner` : `${API}/analysis/market`;
      const response = await axios.post(endpoint);
      
      // Accept both success and fallback analysis
      if (response.data.analysis) {
        setAnalysis(response.data.analysis);
        setAnalysisType(type);
        if (response.data.success) {
          toast.success(type === 'beginner' ? 'Beginner-friendly analysis generated!' : 'Market analysis generated with AI!');
        } else {
          toast.success('Market analysis generated!');
        }
      } else {
        toast.error('Failed to generate analysis');
      }
    } catch (error) {
      console.error('Error generating analysis:', error);
      toast.error('Failed to generate analysis');
    } finally {
      setLoading(false);
    }
  };

  const getSentimentIcon = (sentiment) => {
    const sent = sentiment?.toLowerCase() || 'neutral';
    if (sent.includes('bullish')) return <TrendingUp className="w-5 h-5 text-green-600" />;
    if (sent.includes('bearish')) return <TrendingDown className="w-5 h-5 text-red-600" />;
    return <Minus className="w-5 h-5 text-yellow-600" />;
  };

  const getSentimentColor = (sentiment) => {
    const sent = sentiment?.toLowerCase() || 'neutral';
    if (sent.includes('bullish')) return { backgroundColor: '#DCFCE7', color: '#16A34A' };
    if (sent.includes('bearish')) return { backgroundColor: '#FEE2E2', color: '#DC2626' };
    return { backgroundColor: '#FEF3C7', color: '#D97706' };
  };

  return (
    <Card className="border" style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }} data-testid="market-analysis-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: '#1D4ED8' }}>
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg" style={{ color: '#0F172A' }}>AI Market Analysis</CardTitle>
              <p className="text-sm" style={{ color: '#475569' }}>Powered by Claude AI</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => generateAnalysis('advanced')}
              disabled={loading}
              className="transition-colors"
              style={{ backgroundColor: '#1D4ED8', color: '#FFFFFF' }}
              data-testid="generate-analysis-button"
            >
              {loading && analysisType === 'advanced' ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Analysis
                </>
              )}
            </Button>
            <Button
              onClick={() => generateAnalysis('beginner')}
              disabled={loading}
              className="transition-colors border"
              style={{ backgroundColor: '#16A34A', color: '#FFFFFF', borderColor: '#16A34A' }}
              data-testid="generate-beginner-button"
            >
              {loading && analysisType === 'beginner' ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Learning...
                </>
              ) : (
                <>
                  🤝 Handholding Analysis
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!analysis && !loading && (
          <div className="text-center py-12" style={{ color: '#475569' }}>
            <Brain className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2" style={{ color: '#0F172A' }}>No analysis yet</p>
            <p className="text-sm">Click "Generate Analysis" to get AI-powered market insights</p>
          </div>
        )}

        {loading && (
          <div className="text-center py-12">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-16 h-16 border-4 rounded-full mx-auto mb-4"
              style={{ borderColor: '#1D4ED8', borderTopColor: 'transparent' }}
            />
            <p style={{ color: '#475569' }}>Analyzing market data...</p>
          </div>
        )}

        {analysis && !loading && analysisType === 'beginner' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="beginner-analysis-content"
          >
            {/* Beginner Badge */}
            <div className="flex items-center justify-center gap-2">
              <Badge className="px-4 py-2 text-base" style={{ backgroundColor: '#DCFCE7', color: '#16A34A' }}>
                <span className="text-xl mr-2">🎓</span>
                <span className="font-semibold">Beginner-Friendly Explanation</span>
              </Badge>
            </div>

            {/* What's Happening Today */}
            {analysis.whats_happening && (
              <div className="p-5 rounded-lg border" style={{ backgroundColor: '#F0FDF4', borderColor: '#86EFAC' }}>
                <h3 className="text-base font-bold mb-3 flex items-center gap-2" style={{ color: '#16A34A' }}>
                  <span className="text-xl">📊</span> What's Happening Today
                </h3>
                <p className="leading-relaxed text-base" style={{ color: '#0F172A' }}>{analysis.whats_happening}</p>
              </div>
            )}

            {/* Key Concepts to Learn */}
            {analysis.key_concepts && analysis.key_concepts.length > 0 && (
              <div>
                <h3 className="text-base font-bold mb-3 flex items-center gap-2" style={{ color: '#0F172A' }}>
                  <span className="text-xl">💡</span> Key Concepts to Learn
                </h3>
                <div className="space-y-3">
                  {analysis.key_concepts.map((concept, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="p-4 rounded-lg border"
                      style={{ backgroundColor: '#FEF9C3', borderColor: '#FDE047' }}
                    >
                      <p className="text-base leading-relaxed" style={{ color: '#0F172A' }}>{concept}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* What the Numbers Mean */}
            {analysis.numbers_explained && (
              <div className="p-5 rounded-lg border" style={{ backgroundColor: '#DBEAFE', borderColor: '#93C5FD' }}>
                <h3 className="text-base font-bold mb-3 flex items-center gap-2" style={{ color: '#1D4ED8' }}>
                  <span className="text-xl">🔢</span> What the Numbers Mean
                </h3>
                <p className="leading-relaxed text-base" style={{ color: '#0F172A' }}>{analysis.numbers_explained}</p>
              </div>
            )}

            {/* Should Beginners Worry */}
            {analysis.should_worry && (
              <div className="p-5 rounded-lg border" style={{ backgroundColor: '#FCE7F3', borderColor: '#F9A8D4' }}>
                <h3 className="text-base font-bold mb-3 flex items-center gap-2" style={{ color: '#DB2777' }}>
                  <span className="text-xl">🤔</span> Should Beginners Worry?
                </h3>
                <p className="leading-relaxed text-base" style={{ color: '#0F172A' }}>{analysis.should_worry}</p>
              </div>
            )}

            {/* Learning Tip of the Day */}
            {analysis.learning_tip && (
              <div className="p-5 rounded-lg border" style={{ backgroundColor: '#E0E7FF', borderColor: '#A5B4FC' }}>
                <h3 className="text-base font-bold mb-3 flex items-center gap-2" style={{ color: '#4F46E5' }}>
                  <span className="text-xl">💪</span> Learning Tip of the Day
                </h3>
                <p className="leading-relaxed text-base font-medium" style={{ color: '#0F172A' }}>{analysis.learning_tip}</p>
              </div>
            )}
          </motion.div>
        )}

        {analysis && !loading && analysisType === 'advanced' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="analysis-content"
          >
            {/* Market Sentiment Badge */}
            {analysis.sentiment_indicator && (
              <div className="flex items-center justify-center gap-2">
                <Badge className="px-4 py-2 text-base" style={getSentimentColor(analysis.sentiment_indicator)}>
                  {getSentimentIcon(analysis.sentiment_indicator)}
                  <span className="ml-2 font-semibold">{analysis.sentiment_indicator}</span>
                </Badge>
              </div>
            )}

            {/* Market Overview */}
            {analysis.overview && (
              <div className="p-4 rounded-lg border" style={{ backgroundColor: '#F8FAFC', borderColor: '#E5E7EB' }}>
                <h3 className="text-sm font-semibold mb-2" style={{ color: '#475569' }}>MARKET OVERVIEW</h3>
                <p className="leading-relaxed" style={{ color: '#0F172A' }}>{analysis.overview}</p>
              </div>
            )}

            {/* Key Insights */}
            {analysis.key_insights && analysis.key_insights.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-3" style={{ color: '#475569' }}>KEY INSIGHTS</h3>
                <ul className="space-y-2">
                  {analysis.key_insights.map((insight, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3 p-3 rounded-lg border"
                      style={{ backgroundColor: '#EFF6FF', borderColor: '#BFDBFE' }}
                    >
                      <span className="flex-shrink-0 w-6 h-6 rounded-full text-white text-xs flex items-center justify-center font-bold mt-0.5" style={{ backgroundColor: '#1D4ED8' }}>
                        {index + 1}
                      </span>
                      <span style={{ color: '#0F172A' }}>{insight}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            )}

            {/* Top Performers & Laggards */}
            {analysis.performers && (
              <div className="p-4 rounded-lg border" style={{ backgroundColor: '#F8FAFC', borderColor: '#E5E7EB' }}>
                <h3 className="text-sm font-semibold mb-2" style={{ color: '#475569' }}>PERFORMANCE ANALYSIS</h3>
                <p className="leading-relaxed" style={{ color: '#0F172A' }}>{analysis.performers}</p>
              </div>
            )}

            {/* Market Sentiment Details */}
            {analysis.sentiment && analysis.sentiment !== analysis.sentiment_indicator && (
              <div className="p-4 rounded-lg border" style={{ backgroundColor: '#F8FAFC', borderColor: '#E5E7EB' }}>
                <h3 className="text-sm font-semibold mb-2" style={{ color: '#475569' }}>SENTIMENT ANALYSIS</h3>
                <p className="leading-relaxed" style={{ color: '#0F172A' }}>{analysis.sentiment}</p>
              </div>
            )}

            {/* Outlook */}
            {analysis.outlook && (
              <div className="p-4 rounded-lg border" style={{ backgroundColor: '#DBEAFE', borderColor: '#93C5FD' }}>
                <h3 className="text-sm font-semibold mb-2" style={{ color: '#1D4ED8' }}>OUTLOOK</h3>
                <p className="leading-relaxed" style={{ color: '#0F172A' }}>{analysis.outlook}</p>
              </div>
            )}
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
};

export default MarketAnalysis;
